import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type Placement } from '@atlaskit/popper';

import type {
	ClassificationOption,
	DataClassificationLevelProps,
	ClassificationLevel as PlatformClassificationLevel,
	Color,
} from '@atlassian/data-classification-level';
import DataClassificationLevel from '@atlassian/data-classification-level';

import { LoadableLazy } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ActionSubjectId,
	useTrackClassificationAnalytics,
} from '../hooks/useTrackClassificationAnalytics.ts';
import { TagButtonUIVariant } from '../constants/TagButtonUIVariant.ts';
import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import { getGuidelineProps } from '../utils/getGuidelineProps.ts';

const ClassificationReadViewPopup = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ClassificationReadViewPopup" */ './ClassificationReadViewPopup.tsx'
			)
		).ClassificationReadViewPopup,
});

const ClassificationEditViewPopup = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ClassificationEditViewPopup" */ './ClassificationEditViewPopup.tsx'
			)
		).ClassificationEditViewPopup,
});

export enum TagButtonMenuBehavior {
	readOnly = 'readOnly',
	editOnly = 'editOnly',
	readAndEdit = 'readAndEdit',
}

type GenericClassificationTagButtonProps = {
	spaceKey: string;
	contentId: string;
	contentType: string;
	currentClassification: ClassificationLevel;
	currentClassificationNew?: PlatformClassificationLevel;
	spaceDefaultIsOverridden: boolean;
	onEditCancel?: () => void;
	onEditSubmit?: (c: ClassificationOption) => void;
	loading?: boolean;
	isPopupDisabled?: boolean;
	menuBehavior?: TagButtonMenuBehavior;
	variant?: TagButtonUIVariant;
	popupPlacementOverride?: Placement;
	spacing?: DataClassificationLevelProps['spacing'];
	onRestoreFocus?: () => void;
	buttonTestId?: string;
	source?: string | null;
	canManageSpaceSettings?: boolean;
	classificationControlSetting?: string | null;
	userOverrideSetting?: string | null;
};

const DataClassificationLevelMemo = React.memo(DataClassificationLevel);

const messages = defineMessages({
	tooltipMessage: {
		id: 'data-classification.content-classification.tag-button.tooltip',
		defaultMessage: 'This {contentType} is classified as {classification}',
		description:
			'The text is shown as a tooltip when the user hovers over a classification tag button on content. The placeholder {contentType} will be substituted with the content type (e.g. "page", "whiteboard"), and {classification} will be substituted with the classification level name (e.g. "Confidential", "Public").',
	},
});

export const GenericClassificationTagButton = ({
	spaceKey,
	contentId,
	contentType,
	currentClassification,
	spaceDefaultIsOverridden,
	onEditCancel: onEditCancelProp,
	onEditSubmit: onEditSubmitProp,
	loading = false,
	isPopupDisabled = false,
	menuBehavior = TagButtonMenuBehavior.readOnly,
	variant = TagButtonUIVariant.default,
	popupPlacementOverride,
	spacing,
	onRestoreFocus,
	buttonTestId = 'data-classification-level',
	source,
	canManageSpaceSettings,
	classificationControlSetting,
	userOverrideSetting,
}: GenericClassificationTagButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const [isReadView, setIsReadView] = useState(true);
	const { trackEditPopupViewed } = useTrackClassificationAnalytics();
	const popupPlacement = popupPlacementOverride || 'auto';

	useEffect(() => {
		if (menuBehavior === TagButtonMenuBehavior.editOnly) {
			setIsReadView(false);
		}
	}, [menuBehavior]);

	const defaultSelectedEditOption = spaceDefaultIsOverridden ? currentClassification : undefined;

	const getOnChangeLevelClick: () => (() => void) | undefined = () => {
		if (menuBehavior === TagButtonMenuBehavior.readOnly) {
			return undefined;
		}
		return () => {
			setIsReadView(false);
			handlePopupShown({ isOpen: true, shouldForce: true });
		};
	};

	const onEditCancel = (closePopup: () => void) => {
		onEditCancelProp && onEditCancelProp();
		closePopup();
		if (menuBehavior !== TagButtonMenuBehavior.editOnly) {
			setIsReadView(true);
		}
	};

	const onEditSubmit = (
		classification: ClassificationOption | PlatformClassificationLevel,
		closePopup: () => void,
	) => {
		onEditSubmitProp && onEditSubmitProp(classification);
		onRestoreFocus && onRestoreFocus();
		closePopup();
		if (menuBehavior !== TagButtonMenuBehavior.editOnly) {
			setIsReadView(true);
		}
	};

	const handlePopupShown = ({
		isOpen,
		shouldForce = false,
	}: {
		isOpen: boolean;
		shouldForce?: boolean;
	}) => {
		if (isOpen) {
			const shouldTrackEvent = !isReadView || shouldForce;
			if (shouldTrackEvent) {
				trackEditPopupViewed(ActionSubjectId.CONTENT_TYPE_TITLE_DATA_CLASSIFICATION_POPUP);
			}
		}
	};

	const renderPopupComponent = (closePopup: () => void) => {
		return isReadView ? (
			<ClassificationReadViewPopup
				name={currentClassification.name}
				classification={currentClassification}
				onChangeLevelClick={getOnChangeLevelClick()}
				source={source ?? undefined}
				isCurrentUserSpaceAdmin={canManageSpaceSettings}
				classificationControl={classificationControlSetting ?? 'ANY'}
				userOverride={userOverrideSetting ?? 'ANY'}
			/>
		) : (
			<ClassificationEditViewPopup
				spaceKey={spaceKey}
				selectedOption={defaultSelectedEditOption}
				onEditCancel={() => onEditCancel(closePopup)}
				onEditSubmit={(classification) => onEditSubmit(classification, closePopup)}
				loading={loading}
				cloudId={cloudId}
				contentId={contentId}
				userOverride={userOverrideSetting ?? 'ANY'}
			/>
		);
	};

	const { guideline, guidelineRepresentation } = getGuidelineProps(currentClassification);

	return (
		<>
			<DataClassificationLevelMemo
				testId={buttonTestId}
				name={currentClassification.name}
				guideline={guideline}
				guidelineRepresentation={guidelineRepresentation}
				tooltip={formatMessage(messages.tooltipMessage, {
					contentType,
					classification: currentClassification.name,
				})}
				color={currentClassification.color || ('GREY' as Color)}
				isPopupDisabled={isPopupDisabled}
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				renderPopupComponent={!process.env.REACT_SSR ? renderPopupComponent : undefined}
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				onPopupShown={!process.env.REACT_SSR ? () => handlePopupShown({ isOpen: true }) : undefined}
				isIconOnly={variant === TagButtonUIVariant.iconOnly}
				popupPlacement={popupPlacement}
				spacing={spacing}
			/>
		</>
	);
};
